import { AssetRenderer, Container, TrackingWidget } from '@/components';
import { MasterTranslation } from '@/constants';
import { useGlobalState } from '@/hooks';
import { type IPageOverviewBanner, SectionThemeKey, SectionWrapperVariant } from '@/interfaces';
import { gtmClickOverviewBannerSignups, replaceHighlightString } from '@/utils';
import type React from 'react';
import ButtonGroup from '../../ButtonGroup';
import { BannerContainer, BannerInnerContainer, HeroAssetWrapper } from '../../DefaultBanner/DefaultBanner.styles';
import GraphicImg from '../../GraphicImg';
import WidgetTextSection from '../WidgetTextSection';
import {
  BannerWithTrackingWidgetWrapper,
  ButtonGroupWrapper,
  TrackingWidgetMobileWrapper,
  TrackingWidgetWrapper,
} from './BannerWithTrackingWidget.styles';

const BannerWithTrackingWidget: React.FC<IPageOverviewBanner> = ({
  title,
  titleMobile,
  subText,
  subTextMobile,
  loginTitle,
  loginMobileTitle,
  loginSubtext,
  priority,
  buttons,
  image,
  imageMobile,
  theme,
  heroAsset,
  carrierSlug,
}) => {
  const { isLoggedIn, userInfo, t } = useGlobalState();

  const translatedLoginTitle = t(loginTitle ?? '');
  const translatedLoginMobileTitle = t(loginMobileTitle ?? '');
  const formattedLoginTitle =
    translatedLoginTitle && replaceHighlightString(translatedLoginTitle, 'username', userInfo?.firstName || '');
  const formattedMobileLoginTitle =
    translatedLoginMobileTitle &&
    replaceHighlightString(translatedLoginMobileTitle, 'username', userInfo?.firstName || '');

  const renderTextSection = () => {
    let textObject = {
      mobileTitle: t(titleMobile ?? ''),
      desktopTitle: t(title ?? ''),
      mobileSubText: t(subTextMobile ?? ''),
      desktopSubText: t(subText ?? ''),
    };

    if (isLoggedIn) {
      const defaultLoginTitle = t(MasterTranslation.DefaultOverviewBannerTitle);
      textObject = {
        mobileTitle: userInfo?.firstName && formattedMobileLoginTitle ? formattedMobileLoginTitle : defaultLoginTitle,
        desktopTitle: userInfo?.firstName && formattedLoginTitle ? formattedLoginTitle : defaultLoginTitle,
        mobileSubText: t(loginSubtext ?? ''),
        desktopSubText: t(loginSubtext ?? ''),
      };
    }

    return <WidgetTextSection {...textObject} />;
  };

  return (
    <>
      <BannerWithTrackingWidgetWrapper sectionTheme={theme || SectionThemeKey.Light}>
        <GraphicImg includedWigdet={true} image={image} imageMobile={imageMobile} priority={priority} />
        <Container style={{ position: 'relative' }}>
          <BannerContainer fullWidth={!heroAsset}>
            <BannerInnerContainer>
              {renderTextSection()}
              {!!buttons?.length && (
                <ButtonGroupWrapper>
                  <ButtonGroup
                    buttons={buttons}
                    buttonClick={() => {
                      gtmClickOverviewBannerSignups();
                    }}
                  />
                </ButtonGroupWrapper>
              )}
            </BannerInnerContainer>

            {heroAsset && (
              <HeroAssetWrapper>
                <AssetRenderer
                  asset={heroAsset}
                  imageProps={{
                    width: 540,
                    height: 232,
                    style: { width: 540, height: 232, maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
                    decoding: 'sync',
                  }}
                />
              </HeroAssetWrapper>
            )}
          </BannerContainer>
        </Container>
        <TrackingWidgetWrapper>
          <TrackingWidget variant={SectionWrapperVariant.RowDisplay} carrierSlug={carrierSlug} />
        </TrackingWidgetWrapper>
      </BannerWithTrackingWidgetWrapper>
      <TrackingWidgetMobileWrapper>
        <TrackingWidget variant={SectionWrapperVariant.BlockDisplay} carrierSlug={carrierSlug} />
      </TrackingWidgetMobileWrapper>
    </>
  );
};

export default BannerWithTrackingWidget;
